import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Users, Zap, ChevronRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Full course catalogue ─────────────────────────────────────────────────
const ALL_COURSES = [
  // Term 1 – Core
  { name: 'Managerial Economics', term: 1, type: 'Core', track: 'Management & Strategy' },
  { name: 'Managing Self & Others', term: 1, type: 'Core', track: 'Management & Strategy' },
  { name: 'Building Marketing Strategies', term: 1, type: 'Core', track: 'Sales & Marketing' },
  { name: 'Digital Marketing', term: 1, type: 'Core', track: 'Sales & Marketing' },
  { name: 'Financial Accounting & Analysis', term: 1, type: 'Core', track: 'Finance' },
  { name: 'Statistics for Business', term: 1, type: 'Core', track: 'Data, Tech & Product' },
  { name: 'Scrappy Supply Chains', term: 1, type: 'Core', track: 'Data, Tech & Product' },
  { name: 'Gen AI – Foundations', term: 1, type: 'Core', track: 'Data, Tech & Product' },
  { name: 'Excel for Managers', term: 1, type: 'Core', track: 'Tools & Productivity' },
  { name: 'Arts of Communication', term: 1, type: 'Core', track: 'Communication & Self-Dev' },
  // Term 2 – Core
  { name: 'Macroeconomics', term: 2, type: 'Core', track: 'Management & Strategy' },
  { name: 'Management Strategy', term: 2, type: 'Core', track: 'Management & Strategy' },
  { name: "Before' Zero to One", term: 2, type: 'Core', track: 'Management & Strategy' },
  { name: 'Managerial Accounting', term: 2, type: 'Core', track: 'Finance' },
  { name: 'Corporate Finance', term: 2, type: 'Core', track: 'Finance' },
  { name: 'Data Visualization', term: 2, type: 'Core', track: 'Tools & Productivity' },
  { name: 'Product Management', term: 2, type: 'Core', track: 'Data, Tech & Product' },
  { name: 'No Code Builders Lab', term: 2, type: 'Core', track: 'Data, Tech & Product' },
  { name: 'Product Engineering Lab', term: 2, type: 'Core', track: 'Data, Tech & Product' },
  { name: 'Consumer Behaviour', term: 2, type: 'Core', track: 'Sales & Marketing' },
  { name: 'Business Storytelling & PowerPoint', term: 2, type: 'Core', track: 'Communication & Self-Dev' },
  { name: 'Personal Finance', term: 2, type: 'Core', track: 'Communication & Self-Dev' },
  // Term 3 – Core + Electives
  { name: 'Decoding Startup Business Models', term: 3, type: 'Core', track: 'Management & Strategy' },
  { name: 'Organisational Theory & Design', term: 3, type: 'Elective', track: 'Management & Strategy' },
  { name: 'Financial Modelling', term: 3, type: 'Elective', track: 'Finance' },
  { name: 'Entrepreneurial Finance', term: 3, type: 'Elective', track: 'Finance' },
  { name: 'Market Research', term: 3, type: 'Core', track: 'Sales & Marketing' },
  { name: 'B2B Marketing', term: 3, type: 'Elective', track: 'Sales & Marketing' },
  { name: 'Category Management', term: 3, type: 'Elective', track: 'Sales & Marketing' },
  { name: 'Operations Management', term: 3, type: 'Core', track: 'Data, Tech & Product' },
  { name: 'Design Thinking', term: 3, type: 'Elective', track: 'Data, Tech & Product' },
  { name: 'Product Analytics for Managers', term: 3, type: 'Elective', track: 'Data, Tech & Product' },
  { name: 'Python and AI for Business', term: 3, type: 'Elective', track: 'Tools & Productivity' },
  { name: 'Gen AI – 2', term: 3, type: 'Elective', track: 'Tools & Productivity' },
  // Term 4
  { name: 'Corporate Strategy', term: 4, type: 'Elective', track: 'Management & Strategy' },
  { name: 'Strategic HRM', term: 4, type: 'Elective', track: 'Management & Strategy' },
  { name: 'Behavioural Economics', term: 4, type: 'Elective', track: 'Management & Strategy' },
  { name: 'Project & Stakeholder Management', term: 4, type: 'Elective', track: 'Management & Strategy' },
  { name: 'Investment Analysis & Portfolio Management', term: 4, type: 'Elective', track: 'Finance' },
  { name: 'Blockchain, Crypto & DeFi', term: 4, type: 'Elective', track: 'Finance' },
  { name: 'Valuations', term: 4, type: 'Elective', track: 'Finance' },
  { name: 'Investment Banking', term: 4, type: 'Elective', track: 'Finance' },
  { name: 'Brand Management', term: 4, type: 'Elective', track: 'Sales & Marketing' },
  { name: 'Science of Selling', term: 4, type: 'Elective', track: 'Sales & Marketing' },
  { name: 'Product Strategy with Gamification & Behavioural Design', term: 4, type: 'Elective', track: 'Data, Tech & Product' },
  // Term 5
  { name: 'Corporate Governance', term: 5, type: 'Elective', track: 'Management & Strategy' },
  { name: 'Business Transformation in the Digital Era', term: 5, type: 'Elective', track: 'Management & Strategy' },
  { name: 'Social Entrepreneurship', term: 5, type: 'Elective', track: 'Management & Strategy' },
  { name: 'Derivatives', term: 5, type: 'Elective', track: 'Finance' },
  { name: 'Technical Analysis', term: 5, type: 'Elective', track: 'Finance' },
  { name: 'Competition & Dynamic Pricing', term: 5, type: 'Elective', track: 'Sales & Marketing' },
  { name: 'Sales & Distribution Management', term: 5, type: 'Elective', track: 'Sales & Marketing' },
  { name: 'Product Growth', term: 5, type: 'Elective', track: 'Data, Tech & Product' },
  { name: 'Cybersecurity & Responsible AI', term: 5, type: 'Elective', track: 'Data, Tech & Product' },
  // Term 6
  { name: 'Climate Tech & Sustainability', term: 6, type: 'Elective', track: 'Management & Strategy' },
  { name: 'M&A', term: 6, type: 'Elective', track: 'Finance' },
  { name: 'ESG', term: 6, type: 'Elective', track: 'Finance' },
  { name: 'Evidence-Based Marketing', term: 6, type: 'Elective', track: 'Sales & Marketing' },
  { name: 'Integrated Marketing Communication', term: 6, type: 'Elective', track: 'Sales & Marketing' },
  { name: 'Product Engineering & Execution', term: 6, type: 'Elective', track: 'Data, Tech & Product' },
  // Term 7
  { name: 'Private Equity, VC & Growth Equity', term: 7, type: 'Elective', track: 'Finance' },
  { name: 'New Market Entry & Innovation', term: 7, type: 'Elective', track: 'Management & Strategy' },
  { name: 'Marketing & Retail Analytics', term: 7, type: 'Elective', track: 'Sales & Marketing' },
  { name: 'Marketing Simulation', term: 7, type: 'Elective', track: 'Sales & Marketing' },
];

// ─── Curated mentor pool (top 60 by activity, diverse domains) ─────────────
const MENTOR_POOL = [
  { name: 'Saumya Garg', designation: 'CEO', company: 'Switz Group', linkedin: 'https://www.linkedin.com/in/saumyag/', domain: "Chief of Staff & Founder's Office", expertise: 'Pricing & Revenue Strategy · Corporate Strategy · Growth' },
  { name: 'Rishabh Gupta', designation: 'Lead Product Manager', company: 'Partao', linkedin: 'https://www.linkedin.com/in/rishabh16/', domain: 'Entrepreneurship & Startups', expertise: 'Product Marketing · GTM Strategy · Digital Marketing' },
  { name: 'Mohit Geat', designation: 'Former Consultant', company: 'McKinsey', linkedin: 'https://www.linkedin.com/in/mohit-geat-84bba623/', domain: "Chief of Staff & Founder's Office", expertise: 'Financial Due Diligence · Corporate Strategy · Growth' },
  { name: 'Nitesh Kumar', designation: 'Vice President', company: 'Barclays', linkedin: 'https://www.linkedin.com/in/niteshkmr/', domain: 'Consulting & Strategy', expertise: 'Data Analytics · Financial Modelling · Management Consulting' },
  { name: 'Priyansha Agarwal', designation: 'Sr Product Manager', company: 'Walmart', linkedin: 'https://www.linkedin.com/in/priyansha-agarwal-68062566', domain: 'Consulting & Strategy', expertise: 'GTM Strategy · Startup Fundraising · Product Management' },
  { name: 'Gaurav Jhawar', designation: 'Associate VP', company: 'Ernst & Young', linkedin: 'https://www.linkedin.com/in/gauravjhawar', domain: 'Consulting & Strategy', expertise: 'Venture Capital · Corporate Finance · Valuation' },
  { name: 'Geetika Bajaj', designation: 'DGM, Strategic Initiatives', company: 'Godrej', linkedin: 'https://www.linkedin.com/in/geetika-bajaj-3282421a/', domain: "Chief of Staff & Founder's Office", expertise: 'Growth & Market Entry · Market Research · Consulting' },
  { name: 'Manbir Wahan', designation: 'AVP, New Initiatives', company: 'Swiggy', linkedin: 'https://www.linkedin.com/in/manbirwahan/', domain: "Chief of Staff & Founder's Office", expertise: 'Entrepreneurship · Management Consulting · FinTech' },
  { name: 'Kaushik Joshi', designation: 'Associate Director', company: 'Rapido', linkedin: 'https://www.linkedin.com/in/kaushikjoshi/', domain: "Chief of Staff & Founder's Office", expertise: 'P&L Ownership · Operations · Market Expansion' },
  { name: 'Aakash Sharma', designation: 'Product Lead', company: 'Arcesium', linkedin: 'https://www.linkedin.com/in/aakashsharma2370', domain: 'Data Science & AI', expertise: 'Generative AI · Software Engineering · Data Visualisation' },
  { name: 'Prashakth Kamath', designation: 'Global Marketing Director', company: 'Straive', linkedin: 'https://in.linkedin.com/in/prashakthkamath', domain: 'Sales, Brand & Marketing', expertise: 'AI Tools · Marketing Analytics · Growth Hacking' },
  { name: 'Siddharth Mahanot', designation: 'Co-Founder', company: 'Indifi', linkedin: 'https://www.linkedin.com/in/siddharth-mahanot', domain: 'Entrepreneurship & Startups', expertise: 'Zero-to-One Execution · Green Finance · Partnerships' },
  { name: 'Jatin Dua', designation: 'Product Manager', company: 'Zomato', linkedin: 'https://www.linkedin.com/in/jatindua/', domain: 'Tech & Product', expertise: 'Product Strategy · User Experience · Analytics' },
  { name: 'Prerna Suri', designation: 'Strategy Manager', company: 'BCG', linkedin: 'https://www.linkedin.com/in/prerna-suri/', domain: 'Consulting & Strategy', expertise: 'Management Consulting · Strategy · Market Research' },
  { name: 'Vikram Sood', designation: 'VP Marketing', company: 'Nykaa', linkedin: 'https://www.linkedin.com/in/vikramsood/', domain: 'Sales, Brand & Marketing', expertise: 'Brand Strategy · Performance Marketing · D2C Growth' },
  { name: 'Aparna Gill', designation: 'Investment Associate', company: 'Sequoia Capital', linkedin: 'https://www.linkedin.com/in/aparnagill/', domain: 'PEVC, Banking & Investment', expertise: 'VC Deal Flow · Due Diligence · Startup Evaluation' },
  { name: 'Rohan Mehta', designation: 'Head of Growth', company: 'CRED', linkedin: 'https://www.linkedin.com/in/rohanmehta/', domain: 'Entrepreneurship & Startups', expertise: 'Growth Strategy · User Acquisition · Retention' },
  { name: 'Swati Bhargava', designation: 'Co-Founder', company: 'CashKaro', linkedin: 'https://www.linkedin.com/in/swatibhargava/', domain: 'Entrepreneurship & Startups', expertise: 'Startup Building · Fundraising · D2C Commerce' },
];

// ─── Track colour mapping ──────────────────────────────────────────────────
const TRACK_COLORS = {
  'Management & Strategy':   { bg: 'rgba(63,115,168,0.09)',  border: 'rgba(63,115,168,0.22)',  text: '#1A4F9A' },
  'Finance':                  { bg: 'rgba(63,168,106,0.09)',  border: 'rgba(63,168,106,0.22)',  text: '#2A7050' },
  'Sales & Marketing':        { bg: 'rgba(232,160,32,0.10)',  border: 'rgba(232,160,32,0.25)',  text: '#9A6200' },
  'Data, Tech & Product':     { bg: 'rgba(149,100,220,0.10)', border: 'rgba(149,100,220,0.22)', text: '#6A30BC' },
  'Tools & Productivity':     { bg: 'rgba(217,64,64,0.08)',   border: 'rgba(217,64,64,0.20)',   text: '#B02020' },
  'Communication & Self-Dev': { bg: 'rgba(0,0,0,0.05)',       border: 'rgba(0,0,0,0.12)',       text: 'rgba(0,0,0,0.55)' },
};
const trackStyle = (t) => TRACK_COLORS[t] || TRACK_COLORS['Communication & Self-Dev'];

// ─── Learn by Doing content (static) ───────────────────────────────────────
const TERMS = [
  { term: 1, label: 'TERM 1', project: 'Dropshipping', subtitle: 'Build a Quick Cashflow Business', desc: 'Build a quick cashflow business via arbitrage, no-code, marketing, and sourcing hacks.' },
  { term: 2, label: 'TERM 2', project: 'Pre-seed', subtitle: 'Raise Your First Cheque', desc: 'Raise your first cheque by turning sharp insights into a compelling venture narrative.' },
  { term: 3, label: 'TERM 3', project: 'MVP', subtitle: 'Build a Functional Product', desc: 'Build a functional product that solves one thing, well — without breaking the bank.' },
  { term: 4, label: 'TERM 4', project: 'Creator Challenge + GTM', subtitle: 'Launch Your Product', desc: 'Begin your content creation journey. Launch your product, get your first users, and test traction levers in public.' },
];

// ─── Main component ────────────────────────────────────────────────────────
export const JourneySection = ({ analysisId, initialData }) => {
  const [activeTab, setActiveTab] = useState('courses');
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState('');

  useEffect(() => {
    if (data || !analysisId) return;
    setLoading(true);
    axios.get(`${API}/journey/${analysisId}`)
      .then(r => setData(r.data))
      .catch(() => setError('Could not load recommendations.'))
      .finally(() => setLoading(false));
  }, [analysisId, data]);

  if (loading) return (
    <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', gap: '10px', color: 'rgba(255,255,255,0.70)', fontFamily: 'DM Sans, sans-serif', fontSize: '14px' }}>
      <Loader2 size={16} color="#E8A020" style={{ animation: 'spin 1s linear infinite' }} />
      Building your personalised journey...
    </div>
  );

  if (error || !data) return null;

  const journeyRecommendations = data;

  // Resolve courses: LLM gives course names, we look them up in catalogue
  const recommendedCourseNames = journeyRecommendations.courses || [];
  const courses = recommendedCourseNames
    .map(name => ALL_COURSES.find(c => c.name.toLowerCase() === name.toLowerCase()))
    .filter(Boolean)
    .slice(0, 10);

  // Resolve mentors: LLM gives mentor names, we look them up in pool
  const recommendedMentorNames = journeyRecommendations.mentors || [];
  const mentors = recommendedMentorNames
    .map(name => MENTOR_POOL.find(m => m.name.toLowerCase() === name.toLowerCase()))
    .filter(Boolean)
    .slice(0, 6);

  const TABS = [
    { id: 'courses',  label: 'Top Courses',     icon: BookOpen },
    { id: 'mentors',  label: 'Mentors',          icon: Users   },
    { id: 'doing',    label: 'Learn by Doing',   icon: Zap     },
  ];

  return (
    <div style={{ marginTop: '28px' }}>
      {/* Sub-section divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.10)' }} />
        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>
          Your Journey
        </span>
        <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.10)' }} />
      </div>

      {/* Heading */}
      <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(17px,2.2vw,22px)', fontWeight: 700, color: '#ffffff', lineHeight: 1.25, letterSpacing: '-0.02em', marginBottom: '12px' }}>
        Your Masters' Union journey,{' '}
        <span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontWeight: 500, color: '#E8A020' }}>
          tailored to your profile
        </span>
      </h3>

      {/* Paragraph */}
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(255,255,255,0.90)', lineHeight: 1.7, marginBottom: '22px', maxWidth: '640px' }}>
        Not everyone should use Masters' Union the same way. Based on where you are today and where your profile could go next, here's the path we'd recommend you lean into most — from courses and mentors to the build-by-doing experiences that could shape your trajectory in a much bigger way.
      </p>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '10px', padding: '4px' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              padding: '9px 12px',
              backgroundColor: activeTab === tab.id ? '#ffffff' : 'transparent',
              border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 600,
              color: activeTab === tab.id ? '#111111' : 'rgba(255,255,255,0.50)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        {activeTab === 'courses' && (
          <motion.div key="courses" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.85)', marginBottom: '14px', lineHeight: 1.55 }}>
              Based on your profile, these are the courses we'd strongly recommend you lean into at Masters' Union:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }} className="journey-courses-grid">
              {(courses.length > 0 ? courses : ALL_COURSES.slice(0, 10)).map((course, i) => {
                const ts = trackStyle(course.track);
                return (
                  <div key={i} style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 600, color: '#ffffff', lineHeight: 1.3, margin: 0, flex: 1 }}>
                        {course.name}
                      </p>
                      <span style={{ fontSize: '9px', fontWeight: 700, color: '#E8A020', backgroundColor: 'rgba(232,160,32,0.12)', border: '1px solid rgba(232,160,32,0.22)', borderRadius: '4px', padding: '2px 6px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        T{course.term}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 600, color: ts.text, backgroundColor: ts.bg, border: `1px solid ${ts.border}`, borderRadius: '9999px', padding: '2px 7px', whiteSpace: 'nowrap' }}>
                        {course.track}
                      </span>
                      <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.55)' }}>{course.type}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {activeTab === 'mentors' && (
          <motion.div key="mentors" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.85)', marginBottom: '14px', lineHeight: 1.55 }}>
              We have an on-demand in-house mentorship platform called <strong style={{ color: 'rgba(255,255,255,0.70)' }}>Mentor Union</strong> which hosts more than 500 mentors. Your access enables from Day 1. Following are the top mentors we recommend to you:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }} className="journey-mentors-grid">
              {(mentors.length > 0 ? mentors : MENTOR_POOL.slice(0, 6)).map((m, i) => (
                <div key={i} style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '13px 14px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  {/* Avatar */}
                  <div style={{ width: '38px', height: '38px', borderRadius: '50%', backgroundColor: `hsl(${(i * 47) % 360}, 55%, 45%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 800, color: '#ffffff' }}>
                    {m.name.charAt(0)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 700, color: '#ffffff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {m.name}
                      </p>
                      <a href={m.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.55)', display: 'flex', transition: 'color 0.2s', flexShrink: 0 }}
                        onMouseEnter={e => e.currentTarget.style.color = '#0A66C2'}
                        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.30)'}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/>
                        </svg>
                      </a>
                    </div>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.80)', margin: '0 0 6px' }}>{m.designation} · {m.company}</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {m.expertise.split('·').slice(0, 2).map((e, j) => (
                        <span key={j} style={{ fontSize: '9px', fontWeight: 600, color: '#E8A020', backgroundColor: 'rgba(232,160,32,0.10)', border: '1px solid rgba(232,160,32,0.20)', borderRadius: '9999px', padding: '2px 6px' }}>
                          {e.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === 'doing' && (
          <motion.div key="doing" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.85)', marginBottom: '16px', lineHeight: 1.55 }}>
              Learning by doing is not a choice but an integral part of the curriculum at Masters' Union. Build, test and scale products and businesses with structured guidance across terms:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {TERMS.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '14px 16px' }}>
                  {/* Term badge */}
                  <div style={{ flexShrink: 0, textAlign: 'center', minWidth: '44px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: 'rgba(232,160,32,0.15)', border: '1.5px solid rgba(232,160,32,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 4px' }}>
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 900, color: '#E8A020' }}>{t.term}</span>
                    </div>
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '8px', fontWeight: 700, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t.label}</span>
                  </div>
                  {/* Content */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 700, color: '#E8A020', backgroundColor: 'rgba(232,160,32,0.12)', border: '1px solid rgba(232,160,32,0.25)', borderRadius: '9999px', padding: '2px 8px' }}>
                        {t.project}
                      </span>
                      <ChevronRight size={12} color="rgba(255,255,255,0.25)" />
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 700, color: '#ffffff' }}>{t.subtitle}</span>
                    </div>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(255,255,255,0.82)', lineHeight: 1.55, margin: 0 }}>{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
